import { useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import api from '../api/axios';

export interface IncomingCallData {
  callId: string;
  callerNumber: string;
  clientId?: number;
  clientName?: string;
  clientCreated?: boolean;
  needsName?: boolean;
  dealId?: number;
  dealNumber?: string;
  dealCreated?: boolean;
  extension?: string;
}

const WS_URL = typeof window !== 'undefined' ? (window as any).keyoffDesktop?.serverOrigin ?? window.location.origin : 'http://localhost:3001';

export interface NamePrompt {
  clientId: number;
  dealId?: number;
  phone: string;
}

export function useTelephony(enabled: boolean = true) {
  const [incomingCall, setIncomingCall] = useState<IncomingCallData | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const pendingNames = useRef(new Map<string, NamePrompt>());
  const [namePrompt, setNamePrompt] = useState<NamePrompt | null>(null);

  useEffect(() => {
    if (!enabled) return undefined;
    const socket = io(`${WS_URL}/telephony`, {
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelay: 3000,
    });

    socketRef.current = socket;

    socket.on('incoming_call', (data: IncomingCallData) => {
      if (data.needsName && data.clientId) {
        pendingNames.current.set(data.callId, { clientId: data.clientId, dealId: data.dealId, phone: data.callerNumber });
      }
      setIncomingCall(data);
      // Авто-скрыть через 30 сек если не ответили
      setTimeout(() => {
        setIncomingCall(prev => prev?.callId === data.callId ? null : prev);
      }, 30_000);
    });

    socket.on('call_ended', ({ callId }: { callId: string }) => {
      // звонок закончился (даже если не ответили) — просим указать ФИО нового клиента
      const pending = pendingNames.current.get(callId);
      if (pending) {
        pendingNames.current.delete(callId);
        setNamePrompt((prev) => prev ?? pending);
      }
      setIncomingCall(prev => prev?.callId === callId ? null : prev);
    });

    return () => { socket.disconnect(); };
  }, [enabled]);

  const dismissCall = useCallback(() => setIncomingCall(null), []);

  const answerCall = useCallback(async (callId: string, extension: string): Promise<boolean> => {
    try {
      const res = await api.post('/telephony/answer', { callId, extension });
      return !!res.data?.success;
    } catch (err) {
      console.error('Error answering call:', err);
      return false;
    }
  }, []);

  const closeNamePrompt = useCallback(() => {
    setNamePrompt((prev) => {
      if (prev) {
        for (const [id, p] of Array.from(pendingNames.current.entries())) {
          if (p.clientId === prev.clientId) pendingNames.current.delete(id);
        }
      }
      return null;
    });
  }, []);

  return { incomingCall, dismissCall, answerCall, namePrompt, closeNamePrompt };
}
